import classNames from 'classnames';
import { memo, useCallback, useEffect, useState } from 'react';
import { V2QsNumberProps } from './types';

/**
 * V2QsNumber 컴포넌트
 *
 * 숫자 입력을 위한 컴포넌트
 * 단위 선택, +/- 컨트롤, prefix/suffix 지원
 *
 * @example
 * ```tsx
 * // 기본 사용
 * <V2QsNumber
 *   value={age}
 *   onChange={setAge}
 *   min={0}
 *   max={120}
 *   placeholder="Enter age"
 * />
 *
 * // 단위 선택 포함
 * <V2QsNumber
 *   value={weight}
 *   onChange={setWeight}
 *   units={[
 *     { key: 'kg', label: 'kg' },
 *     { key: 'lbs', label: 'lbs' },
 *   ]}
 *   selectedUnit={unit}
 *   onUnitChange={setUnit}
 * />
 *
 * // +/- 컨트롤 포함
 * <V2QsNumber
 *   value={quantity}
 *   onChange={setQuantity}
 *   min={1}
 *   max={10}
 *   showControls
 * />
 * ```
 */
function V2QsNumber({
  value,
  onChange,
  min,
  max,
  step = 1,
  decimalPlaces,
  placeholder = '',
  error = false,
  errorMessage,
  disabled = false,
  readOnly = false,
  fullWidth = true,
  className,
  containerClassName,
  label,
  hint,
  autoFocus = false,
  units,
  selectedUnit,
  onUnitChange,
  suffix,
  prefix,
  showControls = true,
}: V2QsNumberProps) {
  const [localValue, setLocalValue] = useState<string>(
    value !== undefined && value !== null ? String(value) : ''
  );

  // 무효한 입력 시도 시 에러 상태 관리
  const [invalidInputError, setInvalidInputError] = useState<string | null>(null);

  // 외부 value 변경 시 동기화
  useEffect(() => {
    const newValue = value !== undefined && value !== null ? String(value) : '';
    if (newValue !== localValue) {
      setLocalValue(newValue);
    }
  }, [value]);

  // 값 파싱 및 검증
  const parseValue = useCallback(
    (inputValue: string): number | undefined => {
      if (inputValue === '' || inputValue === '-') return undefined;

      const parsed = parseFloat(inputValue);
      if (Number.isNaN(parsed)) return undefined;

      return parsed;
    },
    []
  );

  // 범위 내 값으로 클램핑
  const clampValue = useCallback(
    (num: number): number => {
      let result = num;
      if (min !== undefined && result < min) result = min;
      if (max !== undefined && result > max) result = max;
      return result;
    },
    [min, max]
  );

  // 소수점 포맷팅
  const formatValue = useCallback(
    (num: number): string => {
      if (decimalPlaces !== undefined) {
        return num.toFixed(decimalPlaces);
      }
      return String(num);
    },
    [decimalPlaces]
  );

  // 입력 변경 핸들러
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      // 빈 값 허용
      if (inputValue === '') {
        setLocalValue('');
        setInvalidInputError(null);
        onChange?.(undefined);
        return;
      }

      // 숫자 및 소수점, 음수 부호만 허용
      const regex = decimalPlaces === 0 ? /^-?\d*$/ : /^-?\d*\.?\d*$/;
      if (!regex.test(inputValue)) {
        // 무효한 입력 시도 시 에러 메시지 표시
        setInvalidInputError('숫자만 입력 가능합니다');
        return;
      }

      setLocalValue(inputValue);

      const parsed = parseValue(inputValue);
      if (parsed !== undefined) {
        // 범위 초과 검사
        if (min !== undefined && parsed < min) {
          setInvalidInputError(`최소값은 ${min}입니다`);
          onChange?.(parsed); // 값은 전달하되 에러 표시
          return;
        }
        if (max !== undefined && parsed > max) {
          setInvalidInputError(`최대값은 ${max}입니다`);
          onChange?.(parsed); // 값은 전달하되 에러 표시
          return;
        }
        // 범위 내 값이면 에러 초기화
        setInvalidInputError(null);
        onChange?.(parsed);
      } else {
        // 파싱 불가능한 경우 (예: "-" 만 입력)
        setInvalidInputError(null);
      }
    },
    [onChange, parseValue, decimalPlaces, min, max]
  );

  // blur 시 범위 검증 및 포맷팅
  const handleBlur = useCallback(() => {
    // blur 시 에러 초기화 (일정 시간 후)
    setInvalidInputError(null);

    const parsed = parseValue(localValue);
    if (parsed !== undefined) {
      const clamped = clampValue(parsed);
      const formatted = formatValue(clamped);
      setLocalValue(formatted);
      if (clamped !== parsed) {
        onChange?.(clamped);
      }
    }
  }, [localValue, parseValue, clampValue, formatValue, onChange]);

  // +/- 버튼 핸들러
  const handleIncrement = useCallback(() => {
    const current = parseValue(localValue) ?? (min ?? 0);
    const newValue = clampValue(current + step);
    const formatted = formatValue(newValue);
    setLocalValue(formatted);
    onChange?.(newValue);
  }, [localValue, parseValue, clampValue, step, formatValue, onChange, min]);

  const handleDecrement = useCallback(() => {
    const current = parseValue(localValue) ?? (min ?? 0);
    const newValue = clampValue(current - step);
    const formatted = formatValue(newValue);
    setLocalValue(formatted);
    onChange?.(newValue);
  }, [localValue, parseValue, clampValue, step, formatValue, onChange, min]);

  // 외부 에러 또는 무효 입력 에러 중 하나라도 있으면 에러 상태
  const hasError = error || !!invalidInputError;
  const displayErrorMessage = errorMessage || invalidInputError;
  const hasUnits = units && units.length > 0;

  // Container 클래스
  const containerClasses = classNames('flex flex-col gap-1', containerClassName);

  // Input wrapper 클래스
  const inputWrapperClasses = classNames(
    'flex items-center transition-all duration-200 rounded-[10px]',
    'border-[1.5px]',
    {
      // Width
      'w-full': fullWidth,
      'w-[200px]': !fullWidth,

      // Border color
      'border-[#E6E9F5]': !hasError && !disabled,
      'border-[#FF5E71]': hasError,
      'hover:border-[#DADDEB]': !disabled && !hasError && !readOnly,
      'focus-within:border-[#4B7BF5]': !disabled && !hasError,

      // Background
      'bg-white': !disabled,
      'bg-[#F2F4FC]': disabled,
    }
  );

  // Input 클래스
  const inputClasses = classNames(
    'flex-1 min-w-0 px-4 py-[17px] bg-transparent outline-none',
    'text-[15px] font-medium leading-[1.6] text-left',
    {
      // Text color
      'text-[#1D1D1F]': !disabled,
      'text-[#AFB2C2]': disabled,

      // Placeholder
      'placeholder:text-[#AFB2C2]': true,

      // Cursor
      'cursor-not-allowed': disabled,
    },
    className
  );

  // Control 버튼 클래스 (위아래 화살표용)
  const arrowButtonClasses = (isDisabled: boolean) =>
    classNames(
      'flex items-center justify-center w-8 h-6 transition-all duration-200',
      {
        'text-[#737580] hover:text-[#4B7BF5] hover:bg-[#F0F4FF]': !isDisabled,
        'text-[#D9DBE5] cursor-not-allowed': isDisabled,
      }
    );

  // 라벨 클래스
  const labelClasses = 'text-sm font-medium text-[#222638] mb-1';

  // 힌트 클래스
  const hintClasses = 'text-xs text-[#AFB2C2]';

  // 에러 메시지 클래스
  const errorClasses = 'text-xs text-[#FF5E71]';

  // Prefix/Suffix 클래스
  const affixClasses = 'text-[15px] text-[#AFB2C2] px-2';

  // Unit 선택 클래스 (inline - 단위가 없을 때)
  const unitSelectInlineClasses = classNames(
    'px-3 py-2 bg-transparent outline-none border-l border-[#E6E9F5]',
    'text-[14px] font-medium text-[#222638]',
    'cursor-pointer',
    {
      'cursor-not-allowed text-[#AFB2C2]': disabled,
    }
  );

  // Unit 선택 클래스 (separate - Frequency 스타일)
  const unitSelectSeparateClasses = classNames(
    'flex-1 min-w-0 px-4 py-[14px] rounded-[10px] transition-all duration-200',
    'border-[1.5px] text-[15px] font-medium',
    'outline-none appearance-none bg-white',
    'bg-no-repeat bg-right',
    {
      'border-[#E6E9F5] focus:border-[#4B7BF5]': !hasError && !disabled,
      'border-[#FF5E71]': hasError,
      'bg-[#F2F4FC]': disabled,
      'text-[#1D1D1F]': !disabled,
      'text-[#AFB2C2]': disabled,
      'cursor-not-allowed': disabled,
      'cursor-pointer': !disabled && !readOnly,
    }
  );

  // 커스텀 화살표 스타일 (Frequency와 동일)
  const selectArrowStyle = {
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23AFB2C2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
    backgroundSize: '20px',
    backgroundPosition: 'right 12px center',
    paddingRight: '40px',
  };

  const currentNum = parseValue(localValue);
  const isAtMin = min !== undefined && currentNum !== undefined && currentNum <= min;
  const isAtMax = max !== undefined && currentNum !== undefined && currentNum >= max;

  // units가 있을 때 Frequency 스타일 레이아웃
  if (hasUnits) {
    return (
      <div className={containerClasses}>
        {/* Label */}
        {label && <label className={labelClasses}>{label}</label>}

        {/* Frequency 스타일 Wrapper */}
        <div className={classNames('flex items-center gap-3 min-w-0', { 'w-full': fullWidth })}>
          {/* 숫자 입력 박스 */}
          <div className={classNames(inputWrapperClasses, 'min-w-0')} style={{ flex: 1 }}>
            {/* Input */}
            <input
              type="text"
              inputMode="decimal"
              value={localValue}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={placeholder}
              disabled={disabled}
              readOnly={readOnly}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={autoFocus}
              className={inputClasses}
            />

            {/* 우측 위아래 화살표 버튼 */}
            {showControls && (
              <div className="flex flex-col border-l border-[#E6E9F5]">
                {/* Increment (위 화살표) */}
                <button
                  type="button"
                  className={arrowButtonClasses(disabled || isAtMax)}
                  onClick={handleIncrement}
                  disabled={disabled || isAtMax}
                >
                  <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 5L5 1L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {/* Decrement (아래 화살표) */}
                <button
                  type="button"
                  className={arrowButtonClasses(disabled || isAtMin)}
                  onClick={handleDecrement}
                  disabled={disabled || isAtMin}
                >
                  <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Unit Select (별도 박스) */}
          <select
            value={selectedUnit}
            onChange={(e) => onUnitChange?.(e.target.value)}
            disabled={disabled}
            className={unitSelectSeparateClasses}
            style={selectArrowStyle}
          >
            {units.map((unit) => (
              <option key={unit.key} value={unit.key}>
                {unit.label}
              </option>
            ))}
          </select>
        </div>

        {/* Hint */}
        {hint && !hasError && <span className={hintClasses}>{hint}</span>}

        {/* Error Message */}
        {hasError && displayErrorMessage && (
          <span className={errorClasses}>{displayErrorMessage}</span>
        )}
      </div>
    );
  }

  // units가 없을 때 기존 레이아웃
  return (
    <div className={containerClasses}>
      {/* Label */}
      {label && <label className={labelClasses}>{label}</label>}

      {/* Input Wrapper */}
      <div className={inputWrapperClasses}>
        {/* Prefix */}
        {prefix && <span className={affixClasses}>{prefix}</span>}

        {/* Input */}
        <input
          type="text"
          inputMode="decimal"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          className={inputClasses}
        />

        {/* Suffix */}
        {suffix && <span className={affixClasses}>{suffix}</span>}

        {/* 우측 위아래 화살표 버튼 */}
        {showControls && (
          <div className="flex flex-col border-l border-[#E6E9F5]">
            {/* Increment (위 화살표) */}
            <button
              type="button"
              className={arrowButtonClasses(disabled || isAtMax)}
              onClick={handleIncrement}
              disabled={disabled || isAtMax}
            >
              <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 5L5 1L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {/* Decrement (아래 화살표) */}
            <button
              type="button"
              className={arrowButtonClasses(disabled || isAtMin)}
              onClick={handleDecrement}
              disabled={disabled || isAtMin}
            >
              <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Hint */}
      {hint && !hasError && <span className={hintClasses}>{hint}</span>}

      {/* Error Message */}
      {hasError && displayErrorMessage && (
        <span className={errorClasses}>{displayErrorMessage}</span>
      )}
    </div>
  );
}

export default memo(V2QsNumber);
